import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  Checkbox,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  CONSTRUCTION_CATEGORIES,
  ConstructionFieldSpec,
  ConstructionSetting,
  ConstructionType,
  changedFields,
  resolveConstructionPreview,
  useCreateConstructionType,
  useUpdateConstructionType,
} from "../../api/constructionTypes";
import { C, formatNum, inputSx, primaryBtnSx } from "./constructionUi";

interface Props {
  open: boolean;
  type: ConstructionType | null;
  settings: ConstructionSetting[];
  fields: ConstructionFieldSpec[];
  onClose: (saved: boolean) => void;
}

export function ConstructionTypeEditor({ open, type, settings, fields, onClose }: Props) {
  const isEdit = !!type;
  const create = useCreateConstructionType();
  const update = useUpdateConstructionType();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (type) {
      setName(type.name);
      setDescription(type.description);
      setSelectedIds(type.settings.map((s) => s.id));
    } else {
      setName("");
      setDescription("");
      setSelectedIds([]);
    }
    setError(null);
  }, [type, open]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Live preview: combine modifiers of all selected characteristics.
  const preview = useMemo(() => {
    const chosen = settings.filter((s) => selectedSet.has(s.id));
    const mods = chosen.flatMap((s) => s.modifiers);
    const resolved = resolveConstructionPreview(mods, fields);
    return changedFields(resolved, fields).map((f) => ({
      label: f.label,
      units: f.units,
      value: resolved[f.name],
    }));
  }, [settings, selectedSet, fields]);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give your construction type a name.");
      return;
    }
    try {
      const body = { name: trimmed, description, setting_ids: selectedIds };
      if (isEdit) await update.mutateAsync({ id: type!.id, body });
      else await create.mutateAsync(body);
      onClose(true);
    } catch (e: any) {
      setError(e?.message || "Failed to save construction type");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161927", color: C.text, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>
        {isEdit ? "Edit construction type" : "New construction type"}
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <Stack spacing={2.5}>
          <TextField
            label="Name"
            placeholder="e.g. Hardened Timber Home"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            sx={inputSx}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={inputSx}
          />

          <Box>
            <Typography sx={{ color: C.text, fontWeight: 600, mb: 0.5 }}>
              Characteristics
            </Typography>
            <Typography variant="caption" sx={{ color: C.faint, mb: 1.5, display: "block" }}>
              Pick the features that describe this construction. They combine
              automatically — order doesn't matter.
            </Typography>

            <Box
              sx={{
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {CONSTRUCTION_CATEGORIES.map((cat, ci) => {
                const inCat = settings.filter((s) => s.category === cat.value);
                if (inCat.length === 0) return null;
                return (
                  <Box key={cat.value}>
                    {ci > 0 && <Divider sx={{ borderColor: C.border }} />}
                    <Typography
                      sx={{
                        color: C.faint,
                        fontSize: "0.66rem",
                        fontWeight: 700,
                        letterSpacing: 0.7,
                        textTransform: "uppercase",
                        px: 1.5,
                        pt: 1.25,
                        pb: 0.5,
                      }}
                    >
                      {cat.label}
                    </Typography>
                    {inCat.map((s) => {
                      const checked = selectedSet.has(s.id);
                      return (
                        <Box
                          key={s.id}
                          onClick={() => toggle(s.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: C.panelHover },
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                            icon={<RadioButtonUncheckedIcon sx={{ color: C.faint }} />}
                            checkedIcon={<CheckCircleIcon sx={{ color: C.accent }} />}
                            sx={{ p: 0 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: C.text }}>
                              {s.name}
                            </Typography>
                            {s.description && (
                              <Typography variant="caption" sx={{ color: C.faint }}>
                                {s.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Live preview */}
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2,
              backgroundColor: C.panel,
              border: `1px solid ${C.border}`,
            }}
          >
            <Typography
              sx={{
                color: C.faint,
                fontSize: "0.66rem",
                fontWeight: 700,
                letterSpacing: 0.7,
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              Resulting fire behaviour
            </Typography>
            {preview.length === 0 ? (
              <Typography variant="caption" sx={{ color: C.faint, fontStyle: "italic" }}>
                Simulator baseline — no characteristics selected yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {preview.map((p) => (
                  <Chip
                    key={p.label}
                    label={`${p.label}: ${formatNum(p.value)}${p.units ? " " + p.units : ""}`}
                    size="small"
                    sx={{
                      height: 22,
                      backgroundColor: C.accentSoft,
                      color: C.accent,
                      "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${C.border}` }}>
        <Button onClick={() => onClose(false)} sx={{ color: C.dim, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={create.isPending || update.isPending}
          sx={primaryBtnSx}
        >
          {isEdit ? "Save changes" : "Create type"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
